import React, { useEffect, useMemo, useState } from "react";
import Card from "components/card";
import {
  MdAdd,
  MdBadge,
  MdCheckCircle,
  MdContacts,
  MdLocalHospital,
  MdMedicalServices,
  MdPeople,
  MdPerson,
  MdPhone,
  MdDelete,
  MdSearch,
} from "react-icons/md";
import { tokenManager } from "utils/tokenManager";

const API_BASE = "https://usibras-web-backend.onrender.com/api/shift-sms/recipients";
const API_NURSE_LEAVES = "https://usibras-web-backend.onrender.com/api/nurse-leaves";
const API_PASSES = "https://usibras-web-backend.onrender.com/api/hr-passes";

const normalizeRecipient = (r) => ({
  id: r.id,
  staffId: r.staff_id,
  fullName: r.full_name,
  contact: r.contact,
  role: r.role,
  type: r.type ?? null,
  roleType: r.role_type ?? null,
  department: r.department ?? null,
});

const roleBadge = (role) =>
  role === "Manager"
    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
    : "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";

const roleTypeBadge = (roleType) =>
  roleType === "Supervisor"
    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
    : "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300";

const getDateString = (date) => date.toISOString().slice(0, 10);

const NurseLeave = () => {
  const [recipients, setRecipients] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [passRecords, setPassRecords] = useState([]);
  const [allActivePassCount, setAllActivePassCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [leavesLoading, setLeavesLoading] = useState(true);
  const [passesLoading, setPassesLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedPersonId, setSelectedPersonId] = useState("");

  const today = new Date();
  const minDate = getDateString(today);
  const maxDate = getDateString(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30));

  const [startDate, setStartDate] = useState(minDate);
  const [endDate, setEndDate] = useState(minDate);
  const [leaveReason, setLeaveReason] = useState("Sick Leave");
  const [notes, setNotes] = useState("");
  const [formMessage, setFormMessage] = useState("");

  const getHeaders = () => {
    const auth = tokenManager.getAuthHeader();
    return { "Content-Type": "application/json", ...(auth ? { Authorization: auth } : {}) };
  };

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load recipients");
      setRecipients((data.data || []).map(normalizeRecipient));
    } catch (error) {
      setRecipients([]);
      setFormMessage(error.message || "Unable to load SMS recipients");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaves = async (query = "") => {
    setLeavesLoading(true);
    try {
      const res = await fetch(`${API_NURSE_LEAVES}${query ? `?${query}` : ""}`, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load leaves");
      const items = (data.data || data || []).map((r) => ({
        id: r.id,
        personId: r.person_id ?? r.personId,
        staffId: r.staff_id ?? r.staffId,
        fullName: r.full_name ?? r.fullName,
        role: r.role,
        roleType: r.role_type ?? r.roleType,
        department: r.department,
        startDate: r.start_date ?? r.startDate,
        endDate: r.end_date ?? r.endDate,
        reason: r.reason,
        notes: r.notes,
        status: r.status ?? 'Pending',
        created_by: r.created_by,
        approved_by: r.approved_by,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));
      setLeaveRequests(items);
      setFormMessage("");
    } catch (err) {
      setLeaveRequests([]);
      setFormMessage(err.message || "Could not load leave requests");
    } finally {
      setLeavesLoading(false);
    }
  };

  const fetchPasses = async (query = "") => {
    setPassesLoading(true);
    try {
      const endpoint = `${API_PASSES}/me${query ? `?${query}` : ""}`;
      const res = await fetch(endpoint, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load passes");
      setPassRecords((data.data || data || []).map((r) => ({
        id: r.id,
        personId: r.person_id ?? r.personId,
        staffId: r.staff_id ?? r.staffId,
        fullName: r.full_name ?? r.fullName,
        role: r.role,
        roleType: r.role_type ?? r.roleType,
        department: r.department,
        startDate: r.start_date ?? r.startDate,
        endDate: r.end_date ?? r.endDate,
        reason: r.reason,
        notes: r.notes,
        status: r.status,
      })));
      setFormMessage("");
    } catch (err) {
      setPassRecords([]);
      setFormMessage(err.message || "Could not load passes");
    } finally {
      setPassesLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipients();
    fetchLeaves();
    fetchPasses();
    fetchAllActivePasses();
  }, []);

  const fetchAllActivePasses = async () => {
    try {
      const endpoint = `${API_PASSES}/hr?status=Granted`;
      const res = await fetch(endpoint, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load active passes");
      const items = (data.data || data || []);
      setAllActivePassCount(items.length);
    } catch (err) {
      setAllActivePassCount(0);
    }
  };

  const filteredRecipients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;

    return recipients.filter((person) => {
      return [
        person.fullName,
        person.staffId,
        person.contact,
        person.role,
        person.roleType,
        person.department,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [recipients, search]);

  const analysisCards = useMemo(() => {
    const staffCount = recipients.filter((person) => person.role === "Staff").length;
    const supervisorCount = recipients.filter((person) => person.roleType === "Supervisor").length;
    const activePassCount = allActivePassCount || passRecords.filter((p) => String(p.status || "").toLowerCase() === "granted").length;

    return [
      { label: "Total recipients", value: recipients.length, icon: MdPeople, color: "text-blue-500 bg-blue-100" },
      { label: "Staff", value: staffCount, icon: MdPerson, color: "text-green-500 bg-green-100" },
      { label: "Supervisors", value: supervisorCount, icon: MdMedicalServices, color: "text-indigo-500 bg-indigo-100" },
      { label: "Active passes", value: activePassCount, icon: MdLocalHospital, color: "text-rose-500 bg-rose-100" },
    ];
  }, [leaveRequests, recipients]);

  const clampDate = (value) => {
    if (!value) return minDate;
    if (value < minDate) return minDate;
    if (value > maxDate) return maxDate;
    return value;
  };

  const handleStartDateChange = (value) => {
    const next = clampDate(value);
    if (next > endDate) setEndDate(next);
    setStartDate(next);
  };

  const handleEndDateChange = (value) => {
    const next = clampDate(value);
    if (next < startDate) setStartDate(next);
    setEndDate(next);
  };

  const handleSubmitLeave = async (e) => {
    e.preventDefault();
    if (!selectedPersonId) {
      setFormMessage("Please select a person before approving leave.");
      return;
    }
    const person = recipients.find((item) => String(item.id) === String(selectedPersonId));
    if (!person) {
      setFormMessage("Selected recipient could not be found.");
      return;
    }

    try {
      const body = {
        full_name: person.fullName,
        staff_id: person.staffId,
        role: person.role,
        role_type: person.roleType,
        department: person.department,
        start_date: startDate,
        end_date: endDate,
        reason: leaveReason,
        notes: notes.trim(),
      };
      const res = await fetch(API_NURSE_LEAVES, { method: "POST", headers: getHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create leave request");
      await fetchLeaves();
      setSelectedPersonId("");
      setStartDate(minDate);
      setEndDate(minDate);
      setLeaveReason("Sick Leave");
      setNotes("");
      setFormMessage(`${person.fullName} has been approved for sick leave (${startDate} → ${endDate}).`);
    } catch (err) {
      setFormMessage(err.message || "Could not create leave request");
    }
  };

  const handleUpdateLeave = async (id, updates = {}) => {
    try {
      const res = await fetch(`${API_NURSE_LEAVES}/${id}`, { method: "PUT", headers: getHeaders(), body: JSON.stringify(updates) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Update failed");
      await fetchLeaves();
      setFormMessage("Leave updated");
    } catch (err) {
      setFormMessage(err.message || "Could not update leave");
    }
  };

  const handleDeletePass = async (id) => {
    if (!window.confirm("Delete this pass record?")) return;
    try {
      const res = await fetch(`${API_PASSES}/${id}`, { method: "DELETE", headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Delete failed");
      await fetchPasses();
      setFormMessage("Pass deleted");
    } catch (err) {
      setFormMessage(err.message || "Could not delete pass");
    }
  };

  const handleGrantPassForSelected = async (e) => {
    e.preventDefault();
    if (!selectedPersonId) {
      setFormMessage("Please select a person before granting a pass.");
      return;
    }
    const person = recipients.find((p) => String(p.id) === String(selectedPersonId));
    if (!person) {
      setFormMessage("Selected recipient could not be found.");
      return;
    }
    if (!window.confirm(`Grant pass for ${person.fullName} (${startDate} → ${endDate})?`)) return;
    try {
      const body = {
        full_name: person.fullName,
        staff_id: person.staffId,
        role: person.role,
        role_type: person.roleType,
        department: person.department,
        start_date: startDate,
        end_date: endDate,
        reason: leaveReason,
        notes: notes.trim(),
      };
      const res = await fetch(API_PASSES, { method: "POST", headers: getHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create pass");
      setFormMessage(`Pass granted for ${person.fullName}`);
    } catch (err) {
      setFormMessage(err.message || "Could not grant pass");
    }
  };

  const handleGrantPassFromLeave = async (leave) => {
    if (!window.confirm(`Grant pass for ${leave.fullName} (${leave.startDate} → ${leave.endDate})?`)) return;
    try {
      const body = {
        full_name: leave.fullName,
        staff_id: leave.staffId,
        role: leave.role,
        role_type: leave.roleType,
        department: leave.department,
        start_date: leave.startDate,
        end_date: leave.endDate,
        reason: leave.reason || "Sick Leave",
        notes: leave.notes || "",
      };
      const res = await fetch(API_PASSES, { method: "POST", headers: getHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create pass");
      setFormMessage(`Pass granted for ${leave.fullName}`);
    } catch (err) {
      setFormMessage(err.message || "Could not grant pass");
    }
  };

  return (
    <div className="mt-5 h-full space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-3">
            <MdLocalHospital className="h-8 w-8 text-rose-500" />
            <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Nurse Pass Records</h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Review SMS recipients and approve sick leave requests for staff members.
          </p>
        </div>
        <div className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600 dark:bg-rose-900/20 dark:text-rose-300">
          {recipients.length} active recipients
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {analysisCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} extra="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
                <p className="mt-2 text-3xl font-bold text-navy-700 dark:text-white">{value}</p>
              </div>
              <div className={`flex h-12 w-12 items-center justify-center rounded-full ${color}`}>
                <Icon className="h-5 w-5" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.4fr_0.8fr]">
        <Card extra="p-0 overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-navy-700">
            <div className="flex items-center gap-2">
              <MdContacts className="h-5 w-5 text-brand-500" />
              <h3 className="text-base font-semibold text-navy-700 dark:text-white">SMS Users</h3>
            </div>
            <div className="relative w-full max-w-xs">
              <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or staff ID"
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-navy-700">
              <thead className="bg-gray-50 dark:bg-navy-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Staff ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Role Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Contact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white dark:divide-navy-700 dark:bg-navy-700">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                      Loading recipients…
                    </td>
                  </tr>
                ) : filteredRecipients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                      No matching recipients found.
                    </td>
                  </tr>
                ) : (
                  filteredRecipients.map((person) => (
                    <tr key={person.id} className="hover:bg-gray-50 dark:hover:bg-navy-600">
                      <td className="px-4 py-3 text-sm font-medium text-navy-700 dark:text-white">{person.staffId || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
                            <MdPerson className="h-4 w-4 text-brand-500" />
                          </div>
                          <span className="text-sm font-medium text-navy-700 dark:text-white">{person.fullName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${roleBadge(person.role)}`}>
                          {person.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {person.roleType ? (
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${roleTypeBadge(person.roleType)}`}>
                            {person.roleType}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {person.department || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                          <MdPhone className="h-4 w-4 text-gray-400" />
                          {person.contact}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card extra="p-6">
          <div className="mb-5 flex items-center gap-2">
            <MdAdd className="h-5 w-5 text-rose-500" />
            <h3 className="text-base font-semibold text-navy-700 dark:text-white">Add Sick Leave</h3>
          </div>

          <form onSubmit={handleSubmitLeave} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Select person</label>
              <select
                value={selectedPersonId}
                onChange={(e) => setSelectedPersonId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              >
                <option value="">Choose recipient…</option>
                {recipients.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.fullName} ({person.staffId || "No ID"})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Start date</label>
                <input
                  type="date"
                  value={startDate}
                  min={minDate}
                  max={maxDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">End date</label>
                <input
                  type="date"
                  value={endDate}
                  min={minDate}
                  max={maxDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Reason</label>
              <input
                type="text"
                value={leaveReason}
                onChange={(e) => setLeaveReason(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Optional note for the nurse/shift team"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              />
            </div>

            {formMessage && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/10 dark:text-green-300">
                {formMessage}
              </div>
            )}

            <button
              type="submit"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-rose-600"
            >
              <MdCheckCircle className="h-4 w-4" />
              Approve Sick Leave
            </button>
            <button
              type="button"
              onClick={handleGrantPassForSelected}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-amber-500 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-100"
            >
              Grant Pass
            </button>
          </form>
        </Card>
      </div>

      <Card extra="p-0 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4 dark:border-navy-700">
          <MdBadge className="h-5 w-5 text-amber-500" />
          <h3 className="text-base font-semibold text-navy-700 dark:text-white">Nurse Pass Records</h3>
          <div className="ml-auto">
            <button onClick={() => fetchPasses()} className="text-sm text-brand-500 hover:underline">Reload</button>
          </div>
        </div>

        {formMessage && (
          <div className="px-6 py-3">
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/10 dark:text-rose-300">{formMessage}</div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-navy-700">
            <thead className="bg-gray-50 dark:bg-navy-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Person</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Staff ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Dates</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-navy-700 dark:bg-navy-700">
              {passesLoading ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">Loading pass records…</td>
                </tr>
              ) : passRecords.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">No pass records yet.</td>
                </tr>
              ) : (
                passRecords.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-navy-600">
                    <td className="px-4 py-3 text-sm font-medium text-navy-700 dark:text-white">{p.fullName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{p.staffId || "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{p.startDate} → {p.endDate}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{p.reason}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-300">{p.status}</span>
                        <button onClick={() => handleDeletePass(p.id)} className="text-sm text-red-600 hover:underline">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default NurseLeave;
